/**
 * Miscellaneous operations admin API module.
 *
 * Extracted from the AdminService god class in services/auth.ts and converted
 * to the central web API client. Covers: invitations, failed VIN scans,
 * override PIN + restriction evaluation, data feed credentials, vehicle
 * queue, and feed configs.
 *
 * Method names, signatures, and observable behavior (error messages, query
 * building, dedup keys) mirror the originals exactly so the composed
 * adminService export stays drop-in compatible.
 */
import type { RoleNotGiven } from '@turn-connect/shared';
import { HttpError } from '@turn-connect/shared';
import type { QueuedVehicle } from '@turn-connect/shared';
import { webApiClient, unwrap } from '../apiClient';
import { dedup, extractItems } from './helpers';
import type { User, Invitation, FailedVINScanListResponse, FailedVINScanStats } from '../auth';
import type { components } from '../../types/api.generated';

// ==========================================
// Invitations
// ==========================================

/**
 * Create a new invitation for a user to join an account
 */
export async function createInvitation(
  accountId: string,
  data: {
    email?: string;
    phone_number?: string;
    first_name: string;
    last_name: string;
    role_assignments: Array<{ role_id: string; location_id?: string; location_group_id?: string; follow_group?: boolean }>;
    user_metadata?: Record<string, unknown>;
    send_now?: boolean;
    send_via?: 'email' | 'sms';
    expiration_days?: number;
  }
): Promise<Invitation> {
  return unwrap(webApiClient.post<Invitation>(`/accounts/${accountId}/invitations`, data), 'Failed to create invitation');
}

/**
 * List invitations for an account
 */
export async function listInvitations(
  accountId: string,
  params?: { status?: string; limit?: number; offset?: number; search?: string }
): Promise<{ items: Invitation[]; total: number }> {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  if (params?.search) query.set('search', params.search);

  const url = `/accounts/${accountId}/invitations${query.toString() ? '?' + query.toString() : ''}`;
  const res = await webApiClient.get<any>(url);
  if (!res.success) {
    throw new Error('Failed to fetch invitations');
  }
  const data = res.data;
  return {
    items: extractItems<Invitation>(data, ['invitations']),
    total: data.total || data.items?.length || 0,
  };
}

/**
 * Get a single invitation by ID
 */
export async function getInvitation(invitationId: string): Promise<Invitation> {
  const res = await webApiClient.get<Invitation>(`/invitations/${invitationId}`);
  if (!res.success) {
    throw new Error('Failed to fetch invitation');
  }
  return res.data as Invitation;
}

/**
 * Send or resend an invitation
 */
export async function sendInvitation(invitationId: string, channel?: 'email' | 'sms'): Promise<void> {
  const body = channel ? { channel } : {};
  await unwrap(webApiClient.post<void>(`/invitations/${invitationId}/send`, body), 'Failed to send invitation');
}

/**
 * Revoke an invitation
 */
export async function revokeInvitation(invitationId: string): Promise<void> {
  // Legacy POST sent no body.
  await unwrap(webApiClient.post<void>(`/invitations/${invitationId}/revoke`, undefined), 'Failed to revoke invitation');
}

/**
 * Verify an invitation token (public endpoint)
 */
export async function verifyInvitation(token: string): Promise<{
  invitation: Invitation;
  account_name: string;
  inviter_name: string;
  role_descriptions: string[];
  existing_user: boolean;
  existing_user_id?: string;
  required_terms?: Array<{ id: string; title: string; version: string }>;
  requires_sms_consent: boolean;
}> {
  return unwrap(webApiClient.get<any>(`/invitations/verify/${token}`), 'Invalid or expired invitation');
}

/**
 * Accept an invitation (public endpoint)
 */
export async function acceptInvitation(
  token: string,
  password?: string,
  smsOptIn?: boolean,
  termsAccepted?: string[]
): Promise<{
  user: User;
  is_new_user: boolean;
  account_id: string;
  account_name: string;
  /** Roles on the invitation that weren't given (the inviter can no longer give them); omitted when all were. */
  roles_not_given?: RoleNotGiven[];
}> {
  const body: Record<string, unknown> = { token };
  if (password) body.password = password;
  if (smsOptIn !== undefined) body.sms_opt_in = smsOptIn;
  if (termsAccepted && termsAccepted.length > 0) body.terms_accepted = termsAccepted;

  return unwrap(webApiClient.post<any>(`/invitations/accept`, body), 'Failed to accept invitation');
}

/**
 * Decline an invitation (public endpoint)
 */
export async function declineInvitation(token: string): Promise<void> {
  // Legacy POST sent no body.
  await unwrap(webApiClient.post<void>(`/invitations/decline/${token}`, undefined), 'Failed to decline invitation');
}

// ── Failed VIN Scans ──

export async function getFailedVINScans(limit = 25, offset = 0): Promise<FailedVINScanListResponse> {
  const res = await webApiClient.get<FailedVINScanListResponse>(`/admin/failed-vin-scans?limit=${limit}&offset=${offset}`);
  if (!res.success) throw new Error('Failed to fetch failed VIN scans');
  return res.data as FailedVINScanListResponse;
}

export async function getFailedVINScanStats(): Promise<FailedVINScanStats> {
  const res = await webApiClient.get<FailedVINScanStats>(`/admin/failed-vin-scans/stats`);
  if (!res.success) throw new Error('Failed to fetch VIN scan stats');
  return res.data as FailedVINScanStats;
}

export function getFailedVINScanImageUrl(id: string): string {
  return `${webApiClient.getBaseUrl()}/admin/failed-vin-scans/${id}/image`;
}

/**
 * Set override PIN for location restrictions
 */
export async function setOverridePIN(locationId: string, pin: string): Promise<void> {
  const res = await webApiClient.put<void>(`/locations/${locationId}/restrictions/override-pin`, { pin });
  if (!res.success) {
    // Legacy behavior: prefer the JSON body's `error` field, else the raw
    // body text, else the fixed message.
    throw new Error(res.errorBody?.error || res.error || 'Failed to set override PIN');
  }
}

/**
 * Evaluate restrictions for a location
 */
export async function evaluateRestrictions(
  locationId: string,
  params: {
    scope: string;
    vehicle_vin?: string;
    service_ids?: string[];
    override_pin?: string;
    work_order_type?: string;
    latitude?: number;
    longitude?: number;
  }
): Promise<{ results: Array<{ rule: string; action: string; message: string; metadata?: Record<string, unknown> }> }> {
  const res = await webApiClient.post<{
    results: Array<{ rule: string; action: string; message: string; metadata?: Record<string, unknown> }>;
  }>(`/locations/${locationId}/restrictions/evaluate`, params);
  if (!res.success) {
    // HttpError rather than plain Error so callers can tell a 403 ("you
    // lack the permission — retrying is pointless") from a network failure
    // ("retry may succeed"). Message keeps the legacy precedence: the JSON
    // body's `error` field, else the raw body text, else the fixed message.
    throw new HttpError(res.status ?? 0, res.errorBody?.error || res.error || 'Failed to evaluate restrictions', res.errorBody);
  }
  return res.data as { results: Array<{ rule: string; action: string; message: string; metadata?: Record<string, unknown> }> };
}

/**
 * True when a restrictions-preflight failure is a permission denial (403 /
 * INSUFFICIENT_PERMISSIONS) rather than a connectivity problem. Callers use
 * this to show "permission denied" instead of "check your connection" —
 * a denied user retrying their wifi can never fix a missing permission.
 */
export function isPermissionDeniedError(e: unknown): boolean {
  return e instanceof HttpError && e.status === 403;
}

// ==========================================
// Data Feed Credentials
// ==========================================

export async function getDataFeeds(accountId: string): Promise<any[]> {
  return dedup(`getDataFeeds:${accountId}`, async () => {
    const res = await webApiClient.get<any[]>(`/accounts/${accountId}/data-feeds`);
    if (!res.success) {
      throw new Error('Failed to fetch data feed credentials');
    }
    return res.data as any[];
  });
}

export async function getDataFeed(accountId: string, feedId: string): Promise<any> {
  return dedup(`getDataFeed:${accountId}:${feedId}`, async () => {
    const res = await webApiClient.get<any>(`/accounts/${accountId}/data-feeds/${feedId}`);
    if (!res.success) {
      throw new Error('Failed to fetch data feed credential');
    }
    return res.data;
  });
}

export async function createDataFeed(
  accountId: string,
  data: { username: string; description?: string; auth_type?: string; ssh_public_key?: string; allowed_ips?: string[] }
): Promise<{ credential: any; password?: string }> {
  return unwrap(
    webApiClient.post<{ credential: any; password?: string }>(`/accounts/${accountId}/data-feeds`, data),
    'Failed to create data feed credential'
  );
}

export async function updateDataFeed(
  accountId: string,
  feedId: string,
  data: { description?: string; is_active?: boolean; ssh_public_key?: string; allowed_ips?: string[] }
): Promise<any> {
  return unwrap(webApiClient.put<any>(`/accounts/${accountId}/data-feeds/${feedId}`, data), 'Failed to update data feed credential');
}

export async function deleteDataFeed(accountId: string, feedId: string): Promise<void> {
  await unwrap(webApiClient.delete<void>(`/accounts/${accountId}/data-feeds/${feedId}`), 'Failed to delete data feed credential');
}

export async function resetDataFeedPassword(accountId: string, feedId: string): Promise<{ credential: any; password: string }> {
  // Legacy POST sent no body.
  return unwrap(
    webApiClient.post<{ credential: any; password: string }>(`/accounts/${accountId}/data-feeds/${feedId}/reset-password`, undefined),
    'Failed to reset data feed password'
  );
}

// ==========================================
// Vehicle Queue
// ==========================================

export async function listVehicleQueue(
  locationId: string,
  params?: { status?: string; search?: string; limit?: number; offset?: number }
): Promise<any> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.search) qs.set('search', params.search);
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.offset) qs.set('offset', String(params.offset));
  const res = await webApiClient.get<any>(`/locations/${locationId}/vehicle-queue?${qs.toString()}`);
  if (!res.success) throw new Error('Failed to list vehicle queue');
  return res.data;
}

/**
 * Look up the vehicle currently queued at this location under a VIN or stock
 * number. Resolves to null when it is not on the list — the common case, and
 * not an error: the caller falls back to a VIN decode.
 *
 * A read only. The vehicle leaves the queue when a work order is actually
 * created for it, never because someone typed its VIN.
 */
export async function lookupQueuedVehicle(
  locationId: string,
  params: { vin?: string; stockNumber?: string }
): Promise<QueuedVehicle | null> {
  const qs = new URLSearchParams();
  if (params.vin) qs.set('vin', params.vin);
  if (params.stockNumber) qs.set('stock_number', params.stockNumber);
  if (!qs.toString()) return null;

  const res = await webApiClient.get<QueuedVehicle>(`/locations/${locationId}/vehicle-queue/lookup?${qs.toString()}`);
  // A 404 (not on the list) and a transport failure land the same way: there
  // is no inventory record to adopt. An inventory outage must never block
  // someone writing a work order.
  if (!res.success || !res.data?.vin) return null;
  return res.data;
}

export async function getVehicleQueueStats(locationId: string): Promise<any> {
  const res = await webApiClient.get<any>(`/locations/${locationId}/vehicle-queue/stats`);
  if (!res.success) throw new Error('Failed to get queue stats');
  return res.data;
}

export async function addToVehicleQueue(locationId: string, payload: any): Promise<any> {
  const res = await webApiClient.post<any>(`/locations/${locationId}/vehicle-queue`, payload);
  if (!res.success) {
    // Legacy behavior: JSON body's `error` field only, else the fixed message.
    throw new Error(res.errorBody?.error || 'Failed to add vehicle to queue');
  }
  return res.data;
}

export async function removeFromVehicleQueue(locationId: string, vehicleId: string): Promise<void> {
  const res = await webApiClient.delete<void>(`/locations/${locationId}/vehicle-queue/${vehicleId}`);
  if (!res.success) throw new Error('Failed to remove vehicle from queue');
}

// ==========================================
// Store inventory feeds
// ==========================================
//
// Feed CRUD moved to locationFeedService: a feed is now a store plus a
// provider, and everything else is inherited. What remains here is the vendor
// onboarding document, which is unchanged — one per store per provider, and
// the only surface carrying the host, username, directory and column mapping,
// because the vendor is the one reading it.

/** Vendor onboarding sheet: structured document + plain-text rendering + email subject. */
export async function getFeedOnboardingDocument(locationId: string, feedId: string): Promise<any> {
  const res = await webApiClient.get<any>(`/locations/${locationId}/feeds/${feedId}/onboarding-document`);
  if (!res.success) throw new Error(res.errorBody?.error || 'Failed to load onboarding document');
  return res.data;
}

export async function downloadFeedOnboardingPdf(locationId: string, feedId: string): Promise<Blob> {
  const res = await webApiClient.get<Blob>(`/locations/${locationId}/feeds/${feedId}/onboarding-document.pdf`);
  if (!res.success) throw new Error(res.errorBody?.error || 'Failed to download onboarding PDF');
  return res.data as Blob;
}

type Schemas = components['schemas'];

/** One address's outcome of a feed setup send (the API's onboardingSendResult, with its always-present fields required). */
export type FeedOnboardingSendResult = Required<Pick<Schemas['handlers.onboardingSendResult'], 'email' | 'status'>> &
  Pick<Schemas['handlers.onboardingSendResult'], 'error'>;

/** A feed setup send: each address's outcome and how many went (the API's onboardingSendResponse). */
export type FeedOnboardingSendResponse = Omit<Schemas['handlers.onboardingSendResponse'], 'results' | 'sent'> & {
  results: FeedOnboardingSendResult[];
  sent: number;
};

/**
 * Emails the feed setup PDF to one or more addresses (separated by commas,
 * semicolons or spaces). Answers each address's outcome, also when none could
 * be sent (502 with results); other errors throw.
 */
export async function sendFeedOnboardingDocument(
  locationId: string,
  feedId: string,
  recipientEmails: string
): Promise<FeedOnboardingSendResponse> {
  const res = await webApiClient.post<FeedOnboardingSendResponse>(`/locations/${locationId}/feeds/${feedId}/onboarding-document/send`, {
    recipient_email: recipientEmails,
  });
  if (!res.success) {
    const results = res.errorBody?.results as FeedOnboardingSendResult[] | undefined;
    if (Array.isArray(results) && results.length) return { results, sent: 0 };
    throw new Error(res.errorBody?.error || 'Failed to send onboarding email');
  }
  const data = res.data as FeedOnboardingSendResponse;
  // An older server answered { sent_to } only.
  if (!Array.isArray(data?.results))
    return { results: data?.sent_to ? [{ email: data.sent_to, status: 'sent' }] : [], sent: data?.sent_to ? 1 : 0 };
  return data;
}

export const miscOpsAdmin = {
  createInvitation,
  listInvitations,
  getInvitation,
  sendInvitation,
  revokeInvitation,
  verifyInvitation,
  acceptInvitation,
  declineInvitation,
  getFailedVINScans,
  getFailedVINScanStats,
  getFailedVINScanImageUrl,
  setOverridePIN,
  evaluateRestrictions,
  getDataFeeds,
  getDataFeed,
  createDataFeed,
  updateDataFeed,
  deleteDataFeed,
  resetDataFeedPassword,
  listVehicleQueue,
  lookupQueuedVehicle,
  getVehicleQueueStats,
  addToVehicleQueue,
  removeFromVehicleQueue,
  getFeedOnboardingDocument,
  downloadFeedOnboardingPdf,
  sendFeedOnboardingDocument,
};
